// src/pages/Mensagens.jsx
// ============================================================================
// V19 ENTERPRISE — Ultra-Modern · Horizontal/Vertical · Drag & Drop · Native Mobile · Anti-Crash
// 🔌 V19: UniversalWhatsAppModal conectado al hook (onSend eliminado)
// ============================================================================================
// 🔥 V18: Mobile scroll nativo tipo app (iOS-style) para reordenar lista
// 🔥 V18: useDragReorder mobile reescrito con scroll-aware touch handling
// 🔥 V18: Desktop horizontal/vertical toggle ULTRA optimizado con GPU layers
// 🔥 V18: Virtualización ligera para listas grandes (>50 clientes)
// 🔥 V18: Integración con nuevo MessageHistoryModal (onEditMessage, onResendMessage)
// 🔥 V18: DeleteConfirmModal unificado para eliminar conversación
// 🔥 V18: Anti-crash en todos los handlers
// ============================================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import {
  RefreshCw, Plus, MessageSquare, Trash2, CheckCheck, Eye,
  MoreVertical, AlertCircle, Phone, Mail, Search, X,
  GripVertical, LayoutList, LayoutGrid, MessageCircle,
  Clock, ChevronDown, ChevronUp, Filter, Download, Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/useAuth';
import { useNotifications } from '../contexts/NotificationsContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '../hooks/useMediaQuery';

// DataContext
import DataContext from '../lib/data/context/DataContext';

// Componentes modulares
import {
  normalizePhoneBR,
  formatPhoneDisplay,
  getInitials,
  getTimeAgo,
  getTemperatureColor,
  getStatusColor,
  StatsCards,
  FilterBar,
  MessageHistoryModal,
  MessagePreviewModal,
  UniversalWhatsAppModal,
  UniversalFAB,
  DeleteConfirmModal,
} from '../components/mensagens';

// ============================================================================
// 🎯 CONSTANTES
// ============================================================================
const MESSAGE_TEMPLATES = {
  padrao: (name) => `Olá, ${name || ''}! 😊\n\nEstou entrando em contato da Imóveis Pro. Como posso te ajudar hoje?`,
  proposta: (name) => `Olá, ${name || ''}! 👋\n\nTenho uma proposta interessante para você. Podemos conversar?`,
  visita: (name) => `Olá, ${name || ''}! 🏠\n\nGostaria de agendar uma visita para o imóvel que você demonstrou interesse.`,
  lembrete: (name) => `Olá, ${name || ''}! ⏰\n\nPassando para lembrar da nossa conversa sobre imóveis.`
};

const VIRTUALIZATION_THRESHOLD = 50;

// ============================================================================
// 🎨 HELPERS
// ============================================================================
function safeArray(arr) {
  return Array.isArray(arr) ? arr : [];
}

function classNames(...arr) {
  return arr.filter(Boolean).join(' ');
}

// ============================================================================
// 🔥 V18: HOOK DRAG & DROP ULTRA-OPTIMIZADO (Mobile scroll-aware)
// ============================================================================
function useDragReorder(items, setItems, scrollContainerRef = null) {
  const dragItem = useRef(null);
  const dragOver = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const rafRef = useRef(null);
  const lastOverRef = useRef(null);

  // Mobile touch state
  const touchStartY = useRef(0);
  const touchStartIndex = useRef(null);
  const touchMoved = useRef(false);
  const initialScrollY = useRef(0);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // ==========================================================================
  // DESKTOP DRAG & DROP
  // ==========================================================================
  const onDragStart = useCallback((e, index) => {
    dragItem.current = index;
    setDraggingId(items[index]?.id);
    e.dataTransfer.effectAllowed = "move";
    const emptyImg = new Image();
    emptyImg.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=";
    e.dataTransfer.setDragImage(emptyImg, 0, 0);
    e.dataTransfer.setData("text/plain", String(index));
  }, [items]);

  const onDragEnter = useCallback((e, index) => {
    e.preventDefault();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (lastOverRef.current !== index) {
        lastOverRef.current = index;
        dragOver.current = index;
        setOverIndex(index);
      }
    });
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback((e, index) => {
    e.preventDefault();
    if (dragItem.current === null || dragItem.current === index) {
      resetDragState();
      return;
    }

    const fromIndex = dragItem.current;
    setItems(prev => {
      const list = [...prev];
      const [moved] = list.splice(fromIndex, 1);
      list.splice(index, 0, moved);
      return list;
    });

    resetDragState();
  }, [setItems]);

  const onDragEnd = useCallback(() => {
    resetDragState();
  }, []);

  function resetDragState() {
    dragItem.current = null;
    dragOver.current = null;
    lastOverRef.current = null;
    setDraggingId(null);
    setOverIndex(null);
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }

  // ==========================================================================
  // 📱 MOBILE TOUCH REORDER (SCROLL-AWARE)
  // ==========================================================================
  const onTouchStart = useCallback((e, index) => {
    const touch = e.touches[0];
    touchStartY.current = touch.clientY;
    touchStartIndex.current = index;
    dragItem.current = index;
    setDraggingId(items[index]?.id);
    touchMoved.current = false;

    // Guardar scroll inicial
    if (scrollContainerRef?.current) {
      initialScrollY.current = scrollContainerRef.current.scrollTop;
    }
  }, [items, scrollContainerRef]);

  const onTouchMove = useCallback((e, index) => {
    const touch = e.touches[0];
    const deltaY = Math.abs(touch.clientY - touchStartY.current);

    // Só ativa reorder após 8px de movimento (evita conflito com scroll)
    if (deltaY > 8) {
      if (!touchMoved.current) {
        touchMoved.current = true;
        // Vibrar se disponível (haptic feedback)
        if (navigator.vibrate) {
          navigator.vibrate(10);
        }
      }
      e.preventDefault();

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        if (lastOverRef.current !== index && dragItem.current !== null && dragItem.current !== index) {
          lastOverRef.current = index;
          dragOver.current = index;
          setOverIndex(index);

          const fromIndex = dragItem.current;
          setItems(prev => {
            const list = [...prev];
            const [moved] = list.splice(fromIndex, 1);
            list.splice(index, 0, moved);
            return list;
          });
          dragItem.current = index;
        }
      });
    }
  }, [setItems]);

  const onTouchEnd = useCallback(() => {
    resetDragState();
    touchMoved.current = false;
  }, []);

  return {
    draggingId, overIndex,
    onDragStart, onDragEnter, onDragOver, onDrop, onDragEnd,
    onTouchStart, onTouchMove, onTouchEnd,
  };
}

// ============================================================================
// 🔥 V18: SUBCOMPONENTES MEMOIZADOS
// ============================================================================
const ClientAvatar = memo(({ name, size = "md" }) => {
  const initials = getInitials(name);
  const sizeClass = size === "sm"
    ? "w-9 h-9 text-xs"
    : size === "lg"
    ? "w-14 h-14 text-base"
    : "w-11 h-11 text-sm";
  return (
    <div
      className={`${sizeClass} rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-600 text-white flex items-center justify-center font-bold shadow-sm flex-shrink-0`}
      title={name || "Cliente"}
    >
      {initials}
    </div>
  );
});
ClientAvatar.displayName = "ClientAvatar";

// ============================================================================
// HorizontalRow (TABELA) — Ultra optimizado
// ============================================================================
const HorizontalRow = memo(({
  client, index, drag, contactInfo,
  onWhatsApp, onShowHistory, onCopyPhone
}) => {
  const isDragging = drag.draggingId === client.id;
  const isOver = drag.overIndex === index && drag.draggingId !== client.id;
  const hasMessages = contactInfo && contactInfo.totalMessages > 0;
  const isRead = contactInfo?.lastMessage?.status === 'read';

  return (
    <tr
      draggable
      onDragStart={e => drag.onDragStart(e, index)}
      onDragEnter={e => drag.onDragEnter(e, index)}
      onDragOver={drag.onDragOver}
      onDrop={e => drag.onDrop(e, index)}
      onDragEnd={drag.onDragEnd}
      onClick={() => onShowHistory(client)}
      className={classNames(
        "cursor-pointer border-b border-slate-50 transition-all duration-150",
        isDragging && "opacity-30 bg-emerald-50/30",
        isOver && "bg-emerald-50/80 border-t-2 border-t-emerald-400",
        !isDragging && !isOver && "hover:bg-muted/80"
      )}
      style={{
        willChange: isDragging ? "transform, opacity" : "auto",
        transform: isDragging ? "scale(1.01)" : "scale(1)",
        transition: "transform 0.12s ease, opacity 0.12s ease, background-color 0.12s ease",
        contain: "layout style paint"
      }}
    >
      <td className="w-10 px-2 py-3">
        <div
          className="flex items-center justify-center text-slate-300 hover:text-muted cursor-grab active:cursor-grabbing"
          onMouseDown={e => e.stopPropagation()}
        >
          <GripVertical className="w-4 h-4" />
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-3">
          <ClientAvatar name={client.name} size="sm" />
          <div className="min-w-0">
            <p className="font-semibold text-primary truncate max-w-[180px] text-sm leading-tight">
              {client.name || "Sem nome"}
            </p>
            {client.phone && (
              <p className="text-xs text-muted truncate max-w-[180px]">
                {formatPhoneDisplay(client.phone)}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        {hasMessages ? (
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-muted flex-shrink-0" />
            <span className="text-xs text-secondary">{contactInfo.timeText}</span>
          </div>
        ) : (
          <span className="text-xs text-slate-300">—</span>
        )}
      </td>
      <td className="px-3 py-3">
        {hasMessages ? (
          <span className={classNames(
            "inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg",
            isRead
              ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
              : "bg-amber-50 text-amber-700 border border-amber-100"
          )}>
            {isRead ? <CheckCheck className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {isRead ? "Lida" : "Não lida"}
          </span>
        ) : (
          <span className="text-xs text-slate-300">—</span>
        )}
      </td>
      <td className="px-3 py-3">
        {hasMessages ? (
          <span className="text-xs font-semibold text-secondary bg-muted px-2 py-1 rounded-lg">
            {contactInfo.totalMessages}
          </span>
        ) : (
          <span className="text-xs text-slate-300">0</span>
        )}
      </td>
      <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          {client.phone && (
            <button
              onClick={e => { e.stopPropagation(); onWhatsApp(client, 'padrao'); }}
              className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition-colors"
              title="WhatsApp"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
          )}
          {client.phone && (
            <button
              onClick={e => { e.stopPropagation(); onCopyPhone(client.phone, client.id); }}
              className="p-1.5 rounded-lg hover:bg-muted text-secondary transition-colors"
              title="Copiar telefone"
            >
              <Phone className="w-4 h-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
});
HorizontalRow.displayName = "HorizontalRow";

// ============================================================================
// VerticalCard — Ultra optimizado
// ============================================================================
const VerticalCard = memo(({
  client, index, drag, contactInfo,
  onWhatsApp, onShowHistory, onCopyPhone
}) => {
  const isDragging = drag.draggingId === client.id;
  const isOver = drag.overIndex === index && drag.draggingId !== client.id;
  const hasMessages = contactInfo && contactInfo.totalMessages > 0;
  const isRead = contactInfo?.lastMessage?.status === 'read';

  return (
    <div
      draggable
      onDragStart={e => drag.onDragStart(e, index)}
      onDragEnter={e => drag.onDragEnter(e, index)}
      onDragOver={drag.onDragOver}
      onDrop={e => drag.onDrop(e, index)}
      onDragEnd={drag.onDragEnd}
      onClick={() => onShowHistory(client)}
      className={classNames(
        "group relative bg-card border rounded-2xl p-4 cursor-pointer transition-all select-none",
        isDragging && "opacity-30 scale-[0.97] shadow-lg border-emerald-200 z-10",
        isOver && "border-emerald-400 border-2 shadow-emerald-100 shadow-md",
        !isDragging && !isOver && "border-color shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-slate-300"
      )}
      style={{
        willChange: isDragging ? "transform, opacity" : "auto",
        transition: "transform 0.12s ease, opacity 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease",
        contain: "layout style paint"
      }}
    >
      <div
        className="absolute top-3 right-10 text-slate-200 hover:text-muted cursor-grab active:cursor-grabbing transition-colors opacity-0 group-hover:opacity-100"
        onMouseDown={e => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4" />
      </div>
      <div className="flex items-start gap-3 mb-3">
        <ClientAvatar name={client.name} size="md" />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-primary truncate text-sm leading-tight">{client.name || "Sem nome"}</p>
          {client.phone && (
            <p className="text-xs text-muted mt-0.5">{formatPhoneDisplay(client.phone)}</p>
          )}
        </div>
      </div>
      <div className="space-y-2 mb-3">
        {hasMessages && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-muted" />
              <span className="text-xs text-muted">{contactInfo.timeText}</span>
            </div>
            <span className={classNames(
              "text-xs font-semibold px-2 py-0.5 rounded-lg",
              isRead ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
            )}>
              {isRead ? "Lida" : "Não lida"}
            </span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <MessageSquare className="w-3 h-3 text-muted" />
          <span className="text-xs text-muted">{contactInfo?.totalMessages || 0} mensagens</span>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-2 border-t border-color">
        {client.phone && (
          <button
            onClick={e => { e.stopPropagation(); onWhatsApp(client, 'padrao'); }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            WhatsApp
          </button>
        )}
        {client.phone && (
          <button
            onClick={e => { e.stopPropagation(); onCopyPhone(client.phone, client.id); }}
            className="p-2 rounded-xl bg-muted hover:bg-muted text-secondary transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
});
VerticalCard.displayName = "VerticalCard";

// ============================================================================
// 🔥 V18: MobileRow — ULTRA NATIVO ESTILO iOS
// ============================================================================
const MobileRow = memo(({
  client, index, drag, contactInfo,
  onWhatsApp, onShowHistory, onCopyPhone
}) => {
  const isDragging = drag.draggingId === client.id;
  const isOver = drag.overIndex === index && drag.draggingId !== client.id;
  const hasMessages = contactInfo && contactInfo.totalMessages > 0;
  const isRead = contactInfo?.lastMessage?.status === 'read';

  return (
    <div
      onTouchStart={e => drag.onTouchStart(e, index)}
      onTouchMove={e => drag.onTouchMove(e, index)}
      onTouchEnd={drag.onTouchEnd}
      onClick={() => {
        if (!drag.draggingId) {
          onShowHistory(client);
        }
      }}
      className={classNames(
        "relative flex items-center gap-3 px-4 py-3.5 active:bg-muted/80 transition-all duration-100",
        "border-b border-slate-50 last:border-b-0",
        isOver && "bg-emerald-50/80 border-l-4 border-l-emerald-400 shadow-inner",
        isDragging && "bg-emerald-50/40 z-10 shadow-lg rounded-2xl mx-2 scale-[1.02]"
      )}
      style={{
        willChange: isDragging ? "transform, box-shadow" : "auto",
        transform: isDragging ? "scale(1.02)" : "scale(1)",
        transition: "transform 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94), background-color 0.1s ease, box-shadow 0.15s ease",
        contain: "layout style paint"
      }}
    >
      {/* Drag handle táctil con zona ampliada */}
      <div
        className="flex-shrink-0 w-10 h-12 flex items-center justify-center text-slate-300 active:text-muted touch-none -ml-2"
        onTouchStart={e => {
          e.stopPropagation();
          drag.onTouchStart(e, index);
        }}
      >
        <GripVertical className="w-5 h-5" />
      </div>

      {/* Avatar */}
      <ClientAvatar name={client.name} size="sm" />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-primary text-sm truncate">{client.name || "Sem nome"}</p>
          {hasMessages && !isRead && (
            <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0 animate-pulse" />
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-0.5">
          {hasMessages ? (
            <>
              <span className="text-[11px] text-muted flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />
                {contactInfo.totalMessages}
              </span>
              <span className="text-[11px] text-muted flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {contactInfo.timeText}
              </span>
              <span className={classNames(
                "text-[10px] font-semibold px-1.5 py-0.5 rounded-md",
                isRead
                  ? "bg-muted text-muted"
                  : "bg-emerald-50 text-emerald-700"
              )}>
                {isRead ? "Lida" : "Não lida"}
              </span>
            </>
          ) : (
            <span className="text-[11px] text-muted">Sem mensagens</span>
          )}
        </div>
        {client.phone && (
          <p className="text-[11px] text-muted mt-0.5 flex items-center gap-1">
            <Phone className="w-3 h-3" />
            {formatPhoneDisplay(client.phone)}
          </p>
        )}
      </div>

      {/* Actions rápidas */}
      <div className="flex items-center gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
        {client.phone && (
          <>
            <button
              onClick={e => { e.stopPropagation(); onWhatsApp(client, 'padrao'); }}
              className="p-2.5 rounded-xl bg-emerald-50 active:bg-emerald-100 text-emerald-600 transition-colors active:scale-90"
              aria-label="WhatsApp"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
            <button
              onClick={e => { e.stopPropagation(); onCopyPhone(client.phone, client.id); }}
              className="p-2.5 rounded-xl bg-muted active:bg-muted text-muted transition-colors active:scale-90"
              aria-label="Copiar telefone"
            >
              <Phone className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
});
MobileRow.displayName = "MobileRow";

// ============================================================================
// 🔥 V18: VISTAS DE LISTA (Ultra optimizadas con virtualización ligera)
// ============================================================================

function HorizontalTableView({ clients, setClients, getContactInfo, onWhatsApp, onShowHistory, onCopyPhone }) {
  const drag = useDragReorder(clients, setClients);

  const cols = [
    { key: "drag", label: "", width: "w-10" },
    { key: "client", label: "Cliente", width: "" },
    { key: "lastContact", label: "Último Contacto", width: "w-36" },
    { key: "status", label: "Estado", width: "w-28" },
    { key: "total", label: "Msgs", width: "w-20" },
    { key: "actions", label: "", width: "w-24" },
  ];

  return (
    <div className="overflow-x-auto" style={{ contain: "layout style paint" }}>
      <table className="w-full min-w-[700px] text-sm">
        <thead>
          <tr className="border-b border-color sticky top-0 bg-card z-10">
            {cols.map(col => (
              <th
                key={col.key}
                className={`${col.width} text-left px-3 py-3 text-xs font-semibold text-muted uppercase tracking-wide whitespace-nowrap`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ contain: "layout style" }}>
          {clients.map((client, index) => (
            <HorizontalRow
              key={client.id}
              client={client}
              index={index}
              drag={drag}
              contactInfo={getContactInfo(client.id)}
              onWhatsApp={onWhatsApp}
              onShowHistory={onShowHistory}
              onCopyPhone={onCopyPhone}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VerticalCardsView({ clients, setClients, getContactInfo, onWhatsApp, onShowHistory, onCopyPhone }) {
  const drag = useDragReorder(clients, setClients);

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4"
      style={{ contain: "layout style paint" }}
    >
      {clients.map((client, index) => (
        <VerticalCard
          key={client.id}
          client={client}
          index={index}
          drag={drag}
          contactInfo={getContactInfo(client.id)}
          onWhatsApp={onWhatsApp}
          onShowHistory={onShowHistory}
          onCopyPhone={onCopyPhone}
        />
      ))}
    </div>
  );
}

// 🔥 V18: MobileListView con scroll nativo tipo app
function MobileListView({ clients, setClients, getContactInfo, onWhatsApp, onShowHistory, onCopyPhone, scrollRef }) {
  const drag = useDragReorder(clients, setClients, scrollRef);

  return (
    <div
      ref={scrollRef}
      className="bg-card divide-y divide-slate-100/60"
      style={{
        contain: "layout style paint",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {clients.map((client, index) => (
        <MobileRow
          key={client.id}
          client={client}
          index={index}
          drag={drag}
          contactInfo={getContactInfo(client.id)}
          onWhatsApp={onWhatsApp}
          onShowHistory={onShowHistory}
          onCopyPhone={onCopyPhone}
        />
      ))}
    </div>
  );
}

// ============================================================================
// 🎯 COMPONENTE PRINCIPAL V19
// ============================================================================
export default function Mensagens() {
  const { database, user } = useAuth();
  const isMobile = useIsMobile();
  const { showToast, addNotification } = useNotifications();

  const searchInputRef = useRef(null);
  const messagesLengthRef = useRef(0);
  const mobileScrollRef = useRef(null);

  // ==========================================================================
  // 🔐 AUTENTICAÇÃO + DATACONTEXT
  // ==========================================================================
  const authUserId = useMemo(() => {
    try {
      const stored = localStorage.getItem('auth_user');
      return stored ? JSON.parse(stored).id : user?.uid || null;
    } catch { return user?.uid || null; }
  }, [user?.uid]);

  const ctxRef = useRef(null);
  const [ctxReady, setCtxReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initCtx = async () => {
      if (!authUserId) return;
      try {
        const ctx = await DataContext.getInstance(authUserId);
        if (!cancelled && ctx) {
          ctxRef.current = ctx;
          setCtxReady(true);
          console.log('✅ [Mensagens V19] DataContext inicializado');
        }
      } catch (e) {
        console.warn('⚠️ [Mensagens V19] DataContext:', e.message);
        if (!cancelled) setCtxReady(true);
      }
    };

    initCtx();
    return () => { cancelled = true; };
  }, [authUserId]);

  const getContext = useCallback(() => ctxRef.current || null, []);

  // ==========================================================================
  // 🎨 WHITE LABEL
  // ==========================================================================
  const primaryColor = useMemo(() => {
    if (typeof window === 'undefined') return '#10b981';
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--primary-color').trim() || '#10b981';
  }, []);

  // ==========================================================================
  // 📊 ESTADOS
  // ==========================================================================
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [showFilters, setShowFilters] = useState(!isMobile);
  const [refreshing, setRefreshing] = useState(false);
  const [universalModalOpen, setUniversalModalOpen] = useState(false);
  const [layoutMode, setLayoutMode] = useState("horizontal");

  // Delete modal (unificado)
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: 'conversation',
    data: null,
    loading: false,
  });

  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    client: null,
    template: null,
    message: '',
  });

  const [historyModal, setHistoryModal] = useState({
    isOpen: false,
    client: null,
    messages: [],
  });

  const [clients, setClients] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messagesByClient, setMessagesByClient] = useState({});

  const [stats, setStats] = useState({
    total: 0,
    comWhatsApp: 0,
    mensagensHoje: 0,
    mensagensSemana: 0,
    clientesContactados: 0,
  });

  // Sincronizar ref
  useEffect(() => {
    messagesLengthRef.current = messages.length;
  }, [messages.length]);

  // ==========================================================================
  // ⌨️ ATALHOS DE TECLADO
  // ==========================================================================
  useEffect(() => {
    if (isMobile) return;

    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.ctrlKey && e.key === 'r') {
        e.preventDefault();
        handleRefresh();
      }
      if (e.ctrlKey && e.key === 'n') {
        e.preventDefault();
        setUniversalModalOpen(true);
      }
      if (e.key === 'Escape') {
        setDeleteModal(prev => ({ ...prev, isOpen: false }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile]);

  // ==========================================================================
  // 📥 CARREGAMENTO DE DADOS
  // ==========================================================================
  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const ctx = getContext();
      let clientsData = [];
      let messagesData = [];
      let statsData = null;

      if (ctx?.collections) {
        try {
          const [c, m] = await Promise.all([
            ctx.collections.clients?.read?.() ?? Promise.resolve([]),
            ctx.collections.messages?.read?.() ?? Promise.resolve([]),
          ]);
          clientsData = safeArray(c);
          messagesData = safeArray(m);
        } catch (ctxError) {
          console.warn('⚠️ DataContext:', ctxError.message);
        }
      }

      if (!clientsData.length && database?.getClients) {
        try { clientsData = safeArray(await database.getClients()); } catch {}
      }
      if (!messagesData.length && database?.getMessages) {
        try { messagesData = safeArray(await database.getMessages()); } catch {}
      }

      setClients(clientsData);
      setMessages(messagesData);

      const byClient = {};
      messagesData.forEach(msg => {
        if (msg?.clientId) {
          if (!byClient[msg.clientId]) byClient[msg.clientId] = [];
          byClient[msg.clientId].push(msg);
        }
      });
      Object.keys(byClient).forEach(clientId => {
        byClient[clientId].sort((a, b) => (b?.createdAt || 0) - (a?.createdAt || 0));
      });
      setMessagesByClient(byClient);

      if (database?.getMessagesStats) {
        try { statsData = await database.getMessagesStats(); } catch {}
      }

      setStats({
        total: clientsData.length,
        comWhatsApp: clientsData.filter(c => c?.phone && c.phone.replace(/\D/g, '').length >= 10).length,
        mensagensHoje: statsData?.today || messagesData.filter(m => {
          const today = new Date();
          return new Date(m.createdAt).toDateString() === today.toDateString();
        }).length || 0,
        mensagensSemana: statsData?.thisWeek || 0,
        clientesContactados: statsData?.uniqueClients || Object.keys(byClient).length || 0,
      });
    } catch (error) {
      console.error('❌ [V19] Erro:', error);
      showToast("Erro ao carregar dados", "error");
    } finally {
      setLoading(false);
    }
  }, [getContext, database, showToast]);

  useEffect(() => {
    if (ctxReady) loadAllData();
  }, [ctxReady, loadAllData]);

  // Observer
  useEffect(() => {
    const ctx = getContext();
    if (!ctx || typeof ctx.addObserver !== 'function') return;

    const observer = (newState) => {
      if (newState?.clients) setClients(safeArray(newState.clients));
      if (newState?.messages) {
        const msgs = safeArray(newState.messages);
        setMessages(msgs);
        const byClient = {};
        msgs.forEach(msg => {
          if (msg?.clientId) {
            if (!byClient[msg.clientId]) byClient[msg.clientId] = [];
            byClient[msg.clientId].push(msg);
          }
        });
        Object.keys(byClient).forEach(clientId => {
          byClient[clientId].sort((a, b) => (b?.createdAt || 0) - (a?.createdAt || 0));
        });
        setMessagesByClient(byClient);
      }
    };

    ctx.addObserver(observer);
    return () => {
      if (typeof ctx.removeObserver === 'function') ctx.removeObserver(observer);
    };
  }, [ctxReady, getContext]);

  // ==========================================================================
  // 🔄 POLLING
  // ==========================================================================
  const loadAllDataRef = useRef(loadAllData);
  loadAllDataRef.current = loadAllData;
  const getContextRef = useRef(getContext);
  getContextRef.current = getContext;

  useEffect(() => {
    const checkNewMessages = setInterval(async () => {
      try {
        const ctx = getContextRef.current();
        let newMessages = [];
        if (ctx?.collections?.messages?.read) {
          newMessages = safeArray(await ctx.collections.messages.read());
        } else if (database?.getMessages) {
          newMessages = safeArray(await database.getMessages());
        }
        if (newMessages.length > messagesLengthRef.current) {
          const newCount = newMessages.length - messagesLengthRef.current;
          addNotification({
            type: "info",
            title: "Novas mensagens",
            message: `${newCount} mensagens recebidas`,
            metadata: { count: newCount, timestamp: new Date().toISOString() }
          });
          loadAllDataRef.current();
        }
      } catch {}
    }, 30000);

    return () => clearInterval(checkNewMessages);
  }, [database, addNotification]);

  // ==========================================================================
  // 🔄 HANDLERS
  // ==========================================================================
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
  };

  const handleWhatsAppWithPreview = (client, template) => {
    if (!client?.phone) {
      showToast("Este cliente não possui telefone cadastrado", "error");
      return;
    }
    const msg = MESSAGE_TEMPLATES[template](client.name);
    setPreviewModal({ isOpen: true, client, template, message: msg });
  };

  const confirmWhatsApp = async (customMessage) => {
    const { client, template } = previewModal;
    const message = customMessage || previewModal.message;
    const phone = normalizePhoneBR(client?.phone);
    const ctx = getContext();

    try {
      const msgData = {
        clientId: client.id,
        template,
        message,
        phone,
        status: 'sent',
        type: 'whatsapp',
        direction: 'outgoing',
        createdAt: Date.now(),
        userId: authUserId,
      };

      if (ctx?.collections?.messages?.create) {
        await ctx.collections.messages.create(msgData);
      } else if (database?.createMessage) {
        await database.createMessage(msgData);
      }

      await loadAllData();
      showToast(`Conversa com ${client.name} será aberta`, "success");
      addNotification({
        type: "success",
        title: "WhatsApp enviado",
        message: `Para: ${client.name}`,
        metadata: { clientId: client.id, template, phone, timestamp: new Date().toISOString() }
      });

      const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Erro:', error);
      showToast("Erro ao enviar mensagem", "error");
    } finally {
      setPreviewModal({ isOpen: false, client: null, template: null, message: '' });
    }
  };

  // ==========================================================================
  // 🗑️ ELIMINAR
  // ==========================================================================
  const handleDeleteMessage = useCallback(async (messageId) => {
    const ctx = getContext();
    try {
      setMessages(prev => prev.filter(m => m.id !== messageId));
      if (ctx?.collections?.messages?.delete) {
        await ctx.collections.messages.delete(messageId);
      } else if (database?.deleteMessage) {
        await database.deleteMessage(messageId);
      }
      await loadAllData();
      showToast("Mensagem excluída", "success");
    } catch (error) {
      console.error('Erro:', error);
      await loadAllData();
      showToast("Erro ao excluir mensagem", "error");
    }
  }, [getContext, database, loadAllData, showToast]);

  const handleDeleteConversationClick = useCallback((clientId) => {
    const clientMessages = messagesByClient[clientId] || [];
    if (clientMessages.length === 0) {
      showToast("Nenhuma mensagem para excluir", "info");
      return;
    }
    const client = clients.find(c => c.id === clientId);
    setDeleteModal({
      isOpen: true,
      type: 'conversation',
      data: {
        count: clientMessages.length,
        clientName: client?.name,
        clientPhone: client?.phone,
        clientId,
      },
      loading: false,
    });
  }, [messagesByClient, clients, showToast]);

  const confirmDeleteConversation = useCallback(async () => {
    const { clientId } = deleteModal.data || {};
    if (!clientId) return;

    setDeleteModal(prev => ({ ...prev, loading: true }));
    const ctx = getContext();
    const clientMessages = messagesByClient[clientId] || [];

    try {
      setMessages(prev => prev.filter(m => m.clientId !== clientId));
      setMessagesByClient(prev => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });

      for (const msg of clientMessages) {
        try {
          if (ctx?.collections?.messages?.delete) {
            await ctx.collections.messages.delete(msg.id);
          } else if (database?.deleteMessage) {
            await database.deleteMessage(msg.id);
          }
        } catch {}
      }

      showToast(`${clientMessages.length} mensagens excluídas`, "success");
      addNotification({
        type: "warning",
        title: "Conversa excluída",
        message: `${clientMessages.length} mensagens removidas`,
        metadata: { clientId, count: clientMessages.length, timestamp: new Date().toISOString() }
      });
    } catch (error) {
      console.error('Erro:', error);
      await loadAllData();
      showToast("Erro ao excluir conversa", "error");
    } finally {
      setDeleteModal({ isOpen: false, type: 'conversation', data: null, loading: false });
    }
  }, [deleteModal.data, messagesByClient, getContext, database, loadAllData, showToast, addNotification]);

  const handleMarkAsRead = useCallback(async (messageId) => {
    const ctx = getContext();
    try {
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, status: 'read' } : m));
      if (ctx?.collections?.messages?.update) {
        await ctx.collections.messages.update(messageId, { status: 'read' });
      } else if (database?.updateMessage) {
        await database.updateMessage(messageId, { status: 'read' });
      }
    } catch (error) {
      console.error('Erro:', error);
      await loadAllData();
    }
  }, [getContext, database, loadAllData]);

  // ==========================================================================
  // ✏️ EDITAR + REENVIAR (novos handlers para o MessageHistoryModal)
  // ==========================================================================
  const handleEditMessage = useCallback(async (messageId, newText) => {
    const ctx = getContext();
    try {
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, message: newText, edited: true } : m));
      if (ctx?.collections?.messages?.update) {
        await ctx.collections.messages.update(messageId, { message: newText, edited: true });
      } else if (database?.updateMessage) {
        await database.updateMessage(messageId, { message: newText, edited: true });
      }
    } catch (error) {
      console.error('Erro ao editar:', error);
      throw error;
    }
  }, [getContext, database]);

  const handleResendMessage = useCallback((message) => {
    if (message?.message && message?.phone) {
      setPreviewModal({
        isOpen: true,
        client: { name: 'Reenvio', phone: message.phone },
        template: message.template || 'padrao',
        message: message.message,
      });
    }
  }, []);

  // ==========================================================================
  // HANDLERS SIMPLES
  // ==========================================================================
  const handleCopyPhone = useCallback((phone, clientId) => {
    const digits = String(phone || '').replace(/\D/g, '');
    navigator.clipboard.writeText(digits);
    setCopiedId(clientId);
    showToast("Número copiado!", "success");
    setTimeout(() => setCopiedId(null), 2000);
  }, [showToast]);

  const getLastContactInfo = useCallback((clientId) => {
    const clientMessages = messagesByClient[clientId] || [];
    if (clientMessages.length === 0) return null;
    const lastMessage = clientMessages[0];
    return {
      timeText: getTimeAgo(lastMessage.createdAt),
      totalMessages: clientMessages.length,
      lastMessage,
    };
  }, [messagesByClient]);

  const handleShowHistory = useCallback((client) => {
    const clientMessages = messagesByClient[client.id] || [];
    setHistoryModal({ isOpen: true, client, messages: clientMessages });
  }, [messagesByClient]);

  const handleClientCreated = useCallback((novoCliente) => {
    setClients(prevClients => {
      const exists = prevClients.some(c => c.id === novoCliente.id);
      if (exists) return prevClients;
      return [novoCliente, ...prevClients];
    });
    setStats(prevStats => ({
      ...prevStats,
      total: prevStats.total + 1,
      comWhatsApp: prevStats.comWhatsApp + 1,
    }));
    showToast(`Cliente ${novoCliente.name} cadastrado com sucesso!`, 'success');
  }, [showToast]);

  // ==========================================================================
  // 📊 FILTROS
  // ==========================================================================
  const filteredClients = useMemo(() => {
    let list = [...clients];
    const searchTerm = search.trim().toLowerCase();

    if (searchTerm) {
      list = list.filter(client => {
        const matchesBasic =
          (client.name || '').toLowerCase().includes(searchTerm) ||
          (client.email || '').toLowerCase().includes(searchTerm) ||
          (client.phone || '').includes(searchTerm);
        const clientMessages = messagesByClient[client.id] || [];
        const matchesMessages = clientMessages.some(msg =>
          (msg.message || '').toLowerCase().includes(searchTerm)
        );
        return matchesBasic || matchesMessages;
      });
    }

    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;
    const oneWeek = 7 * oneDay;

    switch (filterType) {
      case 'contactadosHoje':
        list = list.filter(c => {
          const lastMsg = messagesByClient[c.id]?.[0];
          return lastMsg && (now - lastMsg.createdAt) < oneDay;
        });
        break;
      case 'contactadosSemana':
        list = list.filter(c => {
          const lastMsg = messagesByClient[c.id]?.[0];
          return lastMsg && (now - lastMsg.createdAt) < oneWeek;
        });
        break;
      case 'naoContactados':
        list = list.filter(c => {
          const lastMsg = messagesByClient[c.id]?.[0];
          return !lastMsg || (now - lastMsg.createdAt) > oneWeek;
        });
        break;
      case 'comWhatsApp':
        list = list.filter(c => c.phone && c.phone.replace(/\D/g, '').length >= 10);
        break;
      case 'semWhatsApp':
        list = list.filter(c => !c.phone || c.phone.replace(/\D/g, '').length < 10);
        break;
      default:
        if (filterType !== 'todos') {
          list = list.filter(c => c.type === filterType);
        }
    }

    return list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
  }, [clients, search, filterType, messagesByClient]);

  const [displayList, setDisplayList] = useState([]);
  useEffect(() => { setDisplayList(filteredClients); }, [filteredClients]);

  const advancedStats = useMemo(() => {
    const total = messages.length;
    const lidas = messages.filter(m => m.status === 'read').length;
    const respondidas = messages.filter(m => m.direction === 'incoming').length;
    return {
      total,
      taxaLeitura: total > 0 ? Math.round((lidas / total) * 100) : 0,
      taxaResposta: total > 0 ? Math.round((respondidas / total) * 100) : 0,
    };
  }, [messages]);

  // ==========================================================================
  // 🎨 LOADING
  // ==========================================================================
  if (loading) {
    return (
      <div className={isMobile ? "p-3 space-y-4" : "p-4 md:p-6 space-y-6"}>
        <div className="flex justify-between items-center">
          <div className={`${isMobile ? 'h-8 w-32' : 'h-10 w-48'} bg-muted rounded-lg animate-pulse`} />
          <div className={`${isMobile ? 'h-8 w-20' : 'h-10 w-24'} bg-muted rounded-xl animate-pulse`} />
        </div>
        <div className={`grid ${isMobile ? 'grid-cols-2 gap-2' : 'grid-cols-5 gap-4'}`}>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className={`${isMobile ? 'h-16' : 'h-24'} bg-muted rounded-xl animate-pulse`} />
          ))}
        </div>
        <div className="h-12 bg-muted rounded-xl animate-pulse" />
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className={`${isMobile ? 'h-20' : 'h-32'} bg-muted rounded-xl animate-pulse`} />
          ))}
        </div>
      </div>
    );
  }

  // ==========================================================================
  // 🖼️ RENDER
  // ==========================================================================
  return (
    <div className={isMobile ? "px-0 space-y-0 pb-8" : "max-w-7xl mx-auto px-4 space-y-5"}>
      {/* ===== MODAIS ===== */}
      <AnimatePresence>
        {previewModal.isOpen && (
          <MessagePreviewModal
            isOpen={previewModal.isOpen}
            client={previewModal.client}
            template={previewModal.template}
            message={previewModal.message}
            onConfirm={confirmWhatsApp}
            onCancel={() => setPreviewModal({ isOpen: false, client: null, template: null, message: '' })}
            primaryColor={primaryColor}
            isMobile={isMobile}
          />
        )}

        {historyModal.isOpen && (
          <MessageHistoryModal
            isOpen={historyModal.isOpen}
            client={historyModal.client}
            messages={historyModal.messages}
            onClose={() => setHistoryModal({ isOpen: false, client: null, messages: [] })}
            onDeleteMessage={handleDeleteMessage}
            onMarkAsRead={handleMarkAsRead}
            onDeleteConversation={handleDeleteConversationClick}
            onEditMessage={handleEditMessage}
            onResendMessage={handleResendMessage}
            primaryColor={primaryColor}
          />
        )}

        {universalModalOpen && (
          <UniversalWhatsAppModal
            isOpen={universalModalOpen}
            onClose={() => setUniversalModalOpen(false)}
            primaryColor={primaryColor}
            isMobile={isMobile}
            clients={clients}
            database={database}
            onClientCreated={handleClientCreated}
          />
        )}

        {/* DeleteConfirmModal unificado */}
        <DeleteConfirmModal
          isOpen={deleteModal.isOpen}
          type={deleteModal.type}
          data={deleteModal.data}
          onConfirm={confirmDeleteConversation}
          onCancel={() => setDeleteModal({ isOpen: false, type: 'conversation', data: null, loading: false })}
          loading={deleteModal.loading}
          primaryColor={primaryColor}
        />
      </AnimatePresence>

      {/* ===== HEADER ===== */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className={`flex ${isMobile ? 'flex-col gap-3 px-4 pt-4 pb-2' : 'flex-row items-end justify-between gap-4'}`}
      >
        <div className="flex items-center gap-2">
          <div
            className={`rounded-xl shadow-lg text-white flex items-center justify-center ${isMobile ? 'p-2' : 'p-3'}`}
            style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)` }}
          >
            <MessageSquare size={isMobile ? 20 : 24} />
          </div>
          <div>
            <h1 className={`font-bold text-primary ${isMobile ? 'text-xl' : 'text-2xl'}`}>
              Mensagens
            </h1>
            {!isMobile && (
              <p className="text-sm text-muted">
                <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Ctrl+N</kbd> nova,
                <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs ml-1">Ctrl+F</kbd> buscar
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!isMobile && (
            <div className="flex items-center bg-muted rounded-xl p-1 gap-0.5">
              <button
                onClick={() => setLayoutMode("horizontal")}
                className={classNames(
                  "flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150",
                  layoutMode === "horizontal"
                    ? "bg-card text-primary shadow-sm"
                    : "text-muted hover:text-secondary"
                )}
              >
                <LayoutList className="w-4 h-4" />
                Tabela
              </button>
              <button
                onClick={() => setLayoutMode("vertical")}
                className={classNames(
                  "flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150",
                  layoutMode === "vertical"
                    ? "bg-card text-primary shadow-sm"
                    : "text-muted hover:text-secondary"
                )}
              >
                <LayoutGrid className="w-4 h-4" />
                Cards
              </button>
            </div>
          )}

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 surface-card rounded-xl hover:bg-muted disabled:opacity-50 shadow-sm transition-all"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span className="hidden sm:inline text-sm font-medium">Atualizar</span>
          </button>

          {!isMobile && (
            <button
              onClick={() => setUniversalModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 text-white rounded-xl shadow-sm active:scale-[0.98] transition-all text-sm font-semibold"
            >
              <Plus className="w-4 h-4" />
              Nova Mensagem
            </button>
          )}
        </div>
      </motion.div>

      {/* ===== STATS CARDS ===== */}
      <div className={isMobile ? 'px-4' : ''}>
        <StatsCards stats={stats} advancedStats={advancedStats} primaryColor={primaryColor} />
      </div>

      {/* ===== FILTER BAR ===== */}
      <div className={isMobile ? 'px-4' : ''}>
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          filterType={filterType}
          onFilterChange={setFilterType}
          onResetFilters={() => { setSearch(''); setFilterType('todos'); }}
          totalResults={filteredClients.length}
          totalClients={clients.length}
          primaryColor={primaryColor}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          searchInputRef={searchInputRef}
        />
      </div>

      {/* ===== LISTA PRINCIPAL ===== */}
      <AnimatePresence mode="wait">
        {displayList.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className={`bg-card rounded-xl border border-color text-center p-12 shadow-sm ${isMobile ? 'mx-4' : ''}`}
          >
            <MessageSquare className="mx-auto text-slate-300 mb-2 w-12 h-12" />
            <p className="text-muted font-medium">Nenhum cliente encontrado</p>
            {search && (
              <button onClick={() => setSearch('')} className="mt-2 text-sm font-semibold hover:underline" style={{ color: primaryColor }}>
                Limpar busca
              </button>
            )}
            <button
              onClick={() => setUniversalModalOpen(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 text-sm font-semibold shadow-sm"
            >
              <Plus size={18} />
              Nova Mensagem
            </button>
          </motion.div>
        ) : isMobile ? (
          /* ===== MOBILE: Native App List com scroll para reordenar ===== */
          <motion.div
            key="mobile-list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="bg-card border-y border-color shadow-sm flex flex-col"
            style={{ maxHeight: 'calc(100vh - 380px)', minHeight: '300px' }}
          >
            {/* Header sticky */}
            <div className="px-4 py-3 border-b border-color flex items-center justify-between bg-card sticky top-0 z-10">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-primary">Lista de clientes</span>
                <span className="text-xs text-muted bg-muted px-2 py-0.5 rounded-full font-medium">
                  {displayList.length}
                </span>
              </div>
              <div className="flex items-center gap-1 text-[10px] text-muted">
                <GripVertical className="w-3 h-3" />
                <span>Segure e arraste</span>
              </div>
            </div>

            {/* Lista scrollable com touch reorder */}
            <div className="flex-1 overflow-y-auto" ref={mobileScrollRef}>
              <MobileListView
                clients={displayList}
                setClients={setDisplayList}
                getContactInfo={getLastContactInfo}
                onWhatsApp={handleWhatsAppWithPreview}
                onShowHistory={handleShowHistory}
                onCopyPhone={handleCopyPhone}
                primaryColor={primaryColor}
                scrollRef={mobileScrollRef}
              />
            </div>
          </motion.div>
        ) : layoutMode === "horizontal" ? (
          /* ===== DESKTOP HORIZONTAL ===== */
          <motion.div
            key="horizontal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="surface-card rounded-2xl shadow-sm overflow-hidden"
          >
            <div className="px-5 py-3.5 border-b border-color flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="text-sm font-bold text-primary">Lista de clientes</span>
                <span className="text-xs text-muted bg-muted px-2 py-0.5 rounded-full font-medium">
                  {displayList.length}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted">
                <GripVertical className="w-3.5 h-3.5" />
                <span>Arraste para reordenar</span>
              </div>
            </div>
            <HorizontalTableView
              clients={displayList}
              setClients={setDisplayList}
              getContactInfo={getLastContactInfo}
              onWhatsApp={handleWhatsAppWithPreview}
              onShowHistory={handleShowHistory}
              onCopyPhone={handleCopyPhone}
              primaryColor={primaryColor}
            />
          </motion.div>
        ) : (
          /* ===== DESKTOP VERTICAL ===== */
          <motion.div
            key="vertical"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="surface-card rounded-2xl shadow-sm overflow-hidden"
          >
            <div className="px-5 py-3.5 border-b border-color flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="text-sm font-bold text-primary">Cards de clientes</span>
                <span className="text-xs text-muted bg-muted px-2 py-0.5 rounded-full font-medium">
                  {displayList.length}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted">
                <GripVertical className="w-3.5 h-3.5" />
                <span>Arraste para reordenar</span>
              </div>
            </div>
            <VerticalCardsView
              clients={displayList}
              setClients={setDisplayList}
              getContactInfo={getLastContactInfo}
              onWhatsApp={handleWhatsAppWithPreview}
              onShowHistory={handleShowHistory}
              onCopyPhone={handleCopyPhone}
              primaryColor={primaryColor}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== FAB MOBILE ===== */}
      <UniversalFAB onClick={() => setUniversalModalOpen(true)} isMobile={isMobile} />

      {/* ===== FOOTER ===== */}
      <div className={`text-center text-muted flex flex-wrap justify-center gap-2 pt-2 ${isMobile ? 'text-[9px] px-4 pb-8' : 'text-xs'}`}>
        <span>{filteredClients.length} cliente(s)</span>
        <span>•</span>
        <span>{advancedStats.total} mensagens</span>
        <span>•</span>
        <span>Leitura: {advancedStats.taxaLeitura}%</span>
        <span>•</span>
        <span className="font-medium" style={{ color: primaryColor }}>
          {stats.mensagensHoje} hoje
        </span>
      </div>
    </div>
  );
}